import * as React from 'react';
import {
  DetailsList,
  DetailsRow,
  Selection,
  IColumn,
  IDetailsList,
  IDetailsRowProps,
  SelectionMode,
} from '@fluentui/react/lib/DetailsList';
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { TextField, ITextFieldStyles, ITextField } from '@fluentui/react/lib/TextField';
import { Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions } from '@fluentui/react-components';
import { getTheme, mergeStyles } from '@fluentui/react/lib/Styling';
import { ContextualMenu, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { ShimmeredDetailsList } from '@fluentui/react';

const RESIZE = 'Resize';
const SORT = 'Sort';
const theme = getTheme();
const margin = '0 30px 20px 0';
const controlWrapperClass = mergeStyles({
  display: 'flex',
  flexWrap: 'wrap',
});
const textFieldStyles: Partial<ITextFieldStyles> = {
  root: { margin },
  fieldGroup: { maxWidth: '100px' },
};

interface DetailsListWithResizeProps<T> {
  items: T[];
  columns: IColumn[];
  onRenderItemColumn?: (item?: T, index?: number, column?: IColumn) => JSX.Element | string | undefined;
  setKey?: string;
  selection?: Selection;
  componentRef?: React.RefObject<IDetailsList>;
  selectionMode?: SelectionMode;
  enableShimmer?: boolean;
  ariaLabelForShimmer?: string;
  ariaLabelForGrid?: string;
  ariaLabelForSelectAllCheckbox?: string;
  checkButtonAriaLabel?: string;
  onRenderRow?: (props?: IDetailsRowProps) => JSX.Element | null;
  enableResize?: boolean;
  enableSort?: boolean;
}

export const DetailsListWithResize = <T,>({
  items,
  columns: initialColumns,
  onRenderItemColumn,
  setKey,
  selection,
  componentRef,
  selectionMode = SelectionMode.none,
  enableShimmer = false,
  ariaLabelForGrid,
  ariaLabelForShimmer,
  ariaLabelForSelectAllCheckbox,
  checkButtonAriaLabel,
  onRenderRow,
  enableResize = true,
  enableSort = true,
}: DetailsListWithResizeProps<T>): JSX.Element => {
  const [columns, setColumns] = React.useState<IColumn[]>(initialColumns);
  const [isDialogHidden, setIsDialogHidden] = React.useState(true);
  const [contextualMenuProps, setContextualMenuProps] = React.useState<IContextualMenuProps | undefined>(undefined);
  const [sortedItems, setSortedItems] = React.useState<T[]>(items);
  const [sortDirection, setSortDirection] = React.useState<{ [key: string]: 'asc' | 'desc' }>({}); // Track sort direction for each column

  const textfieldRef = React.useRef<ITextField>(null);
  const columnToEdit = React.useRef<IColumn | null>(null);
  const clickHandler = React.useRef<string>(RESIZE);

  React.useEffect(() => {
    setSortedItems(items)
  }, [items]);

  const resizeColumn = (column: IColumn) => {
    columnToEdit.current = column;
    clickHandler.current = RESIZE;
    showDialog();
  };

  const sortColumn = (column: IColumn) => {
    const fieldName = column.fieldName as keyof T;

    // Determine the new sort direction
    const currentDirection = sortDirection[fieldName as string] || 'asc';
    const newDirection = currentDirection === 'asc' ? 'desc' : 'asc';

    // Sort items based on the new direction
    const sorted = [...sortedItems].sort((a, b) => {
      if (a[fieldName] < b[fieldName]) return newDirection === 'asc' ? -1 : 1;
      if (a[fieldName] > b[fieldName]) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Update state
    setSortedItems(sorted);
    setSortDirection({ ...sortDirection, [fieldName as string]: newDirection });
  };

  const confirmDialog = () => {
    if (textfieldRef.current && columnToEdit.current) {
      const input = Number(textfieldRef.current.value || 0);
      if (clickHandler.current === RESIZE) {
        columnToEdit.current.minWidth = input;
        columnToEdit.current.maxWidth = input;
        setColumns([...columns]);
      }
    }
    hideDialog();
  };

  const hideDialog = () => setIsDialogHidden(true);

  const showDialog = () => setIsDialogHidden(false);

  const onColumnKeyDown = (ev: React.KeyboardEvent, column: IColumn): void => {
    if (ev.ctrlKey) {
      ev.preventDefault();
      if (ev.key === 'ArrowLeft') {
        column.minWidth = Math.max((column.minWidth || 100) - 10, 50);
        column.maxWidth = column.minWidth;
      } else if (ev.key === 'ArrowRight') {
        column.minWidth = Math.min((column.minWidth || 100) + 10, 500);
        column.maxWidth = column.minWidth;
      }
      setColumns([...columns]);
    }
  };

  const onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    const menuItems = [];

    if (enableResize) {
      menuItems.push({ key: 'resize', text: 'Resize', onClick: () => resizeColumn(column) });
    }

    if (enableSort) {
      menuItems.push({ key: 'sort', text: 'Sort', onClick: () => sortColumn(column) });
    }

    if (menuItems.length > 0) {
      setContextualMenuProps({
        items: menuItems,
        target: ev.currentTarget as HTMLElement,
        gapSpace: 10,
        isBeakVisible: true,
        onDismiss: () => setContextualMenuProps(undefined),
      });
    }
  };

  const dialogContentProps = {
    title: 'Resize Column',
    closeButtonAriaLabel: 'Close',
  };

  return (
    <div>
        <ShimmeredDetailsList
          items={sortedItems}
          columns={columns.map((col) => ({
            ...col,
            onColumnKeyDown: (ev) => onColumnKeyDown(ev, col),
            onColumnClick: (ev) => {
              if (enableResize || enableSort) {
                onColumnClick(ev, col);
              }
            },
          }))}
          setKey={setKey}
          selection={selection}
          componentRef={componentRef}
          selectionMode={selectionMode}
          enableShimmer={enableShimmer}
          selectionPreservedOnEmptyClick={true}
          onRenderItemColumn={onRenderItemColumn}
          onRenderRow={onRenderRow}
          ariaLabelForShimmer={ariaLabelForShimmer}
          ariaLabelForGrid={ariaLabelForGrid}
          ariaLabelForSelectAllCheckbox={ariaLabelForSelectAllCheckbox}
          checkButtonAriaLabel={checkButtonAriaLabel}
        />
      {contextualMenuProps && <ContextualMenu {...contextualMenuProps} />}
      <Dialog open={!isDialogHidden} onOpenChange={(event, data) => data.open ? showDialog() : hideDialog()}>
        <DialogSurface style={{ maxWidth: 450 }}>
          <DialogBody>
            <DialogTitle>{dialogContentProps.title}</DialogTitle>
            <DialogContent>
              Enter desired column width in pixels:
            </DialogContent>
            <TextField componentRef={textfieldRef} aria-label="Enter column width" />
            <DialogActions>
              <Button appearance="primary" onClick={confirmDialog}>Confirm</Button>
              <Button onClick={hideDialog}>Cancel</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};